import React, { useEffect } from 'react';
import {
    Flex,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Tooltip,
    ActionIcon,
    Badge,
} from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { IconSearch, IconTrash } from '@tabler/icons-react';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { useElementSize } from '@mantine/hooks';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';
import { modals } from '@mantine/modals';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { RouteStore } from '../../../global/Store/RouteStore.ts';
import { 
    usePatientDelegations, 
    useRevokeDelegation
} from '../../../global/hooks/useHcpDelegation.tsx';
import { 
    IDelegationModel, 
    ConsentStatus
} from '../../../global/apis/HcpDelegationApi/HcpDelegationApi.ts';

export type IInternalCollaboratorTableProps = {
    patientStore?: PatientStore;
    routeStore?: RouteStore;
};

const InternalCollaboratorTable: React.FC<IInternalCollaboratorTableProps> = inject(
    'patientStore',
    'routeStore',
)(
    observer(({ patientStore, routeStore }) => {
        const { t } = useTranslation();
        
        const patientId = patientStore?.patientId ? parseInt(patientStore.patientId) : 0;
        
        const { data: delegations, isLoading } = usePatientDelegations(patientId);
        const revokeDelegation = useRevokeDelegation();

        const { setSortedData, sortedData, sortBy, reverseSortDirection, setSorting, search, setSearch } = useTable();
        const { ref, width } = useElementSize();

        useEffect(() => {
            setSortedData([]);
            if (delegations?.length) 
                setSortedData(delegations);
            
        }, [delegations, setSortedData]);

        useEffect(() => {
            routeStore?.setFromLocation();
        }, [routeStore]);

        const handleRevokeDelegation = (delegation: IDelegationModel) => {
            modals.openConfirmModal({
                title: t('delegation.revoke.title'),
                children: (
                    <Stack gap="sm">
                        <Text>{t('delegation.revoke.confirmation')}</Text>
                        <Text size="sm" c="dimmed">
                            {delegation.delegatedHcpName}
                        </Text>
                    </Stack>
                ),
                labels: { confirm: t('delegation.revoke.confirm'), cancel: t('button.cancel') },
                confirmProps: { color: 'red' },
                cancelProps: { variant: 'outline', color: 'gray' },
                onConfirm: () => revokeDelegation.mutate({ delegationId: delegation.id }),
                size: 'lg',
                radius: 'xs',
            });
        };

        const getConsentStatusBadge = (status: ConsentStatus) => {
            const statusConfig = {
                [ConsentStatus.Pending]: { color: 'yellow', label: t('delegation.status.pending') },
                [ConsentStatus.Granted]: { color: 'green', label: t('delegation.status.granted') },
                [ConsentStatus.Revoked]: { color: 'red', label: t('delegation.status.revoked') },
                [ConsentStatus.Expired]: { color: 'gray', label: t('delegation.status.expired') },
            };

            const config = statusConfig[status];
            return <Badge color={config.color} size="sm">{config.label}</Badge>;
        };

        if (isLoading) {
            return (
                <Paper p="md">
                    <Text>{t('global.loading')}</Text>
                </Paper>
            );
        }

        return (
            <Paper ref={ref} h={'min-content'} w={'100%'}>
                <Stack h={'100%'}>
                    <Flex align={'center'} gap={'sm'}>
                        <TextInput
                            hidden={width < 450}
                            placeholder={t('global.search')}
                            rightSection={width < 450 ? null : <IconSearch />}
                            value={search}
                            color={'var(--mantine-color-default-color)'}
                            style={{ flex: 1 }}
                            radius={'md'}
                            onChange={(el) => setSearch(el.currentTarget.value)}
                        />
                    </Flex>

                    {!sortedData?.length ? (
                        <Stack align="center" justify="center" py="xl">
                            <Text c="dimmed" size="sm">
                                {t('delegation.patient.noDelegations')}
                            </Text>
                        </Stack>
                    ) : (
                        <ScrollArea w="100%">
                            <Table striped highlightOnHover style={{ tableLayout: 'auto' }}>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Th
                                            sorted={sortBy === 'delegatedHcpName'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('delegatedHcpName', sortedData)}
                                        >
                                            {t('delegation.table.hcpName')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'delegatedHcpSpecialty'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('delegatedHcpSpecialty', sortedData)}
                                        >
                                            {t('delegation.table.specialty')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'patientConsentStatus'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('patientConsentStatus', sortedData)}
                                        >
                                            {t('delegation.table.consentStatus')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'createdDate'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('createdDate', sortedData)}
                                        >
                                            {t('delegation.table.createdDate')}
                                        </Th>
                                        <Table.Th style={{width: 80}}>{t('global.actions')}</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {sortedData
                                        .filter((delegation: IDelegationModel) => {
                                            if (!search) return true;
                                            const specialtyName = delegation.healthcareProfessionalTypes?.find(
                                                type => type.id === delegation.delegatedHcpSpecialty
                                            )?.name || '';
                                            return containsStringIgnoreCase([delegation.delegatedHcpName, specialtyName], search);
                                        })
                                        .map((delegation: IDelegationModel) => (
                                            <Table.Tr key={delegation.id}>
                                                <Table.Td>
                                                    <Text fw={500}>{delegation.delegatedHcpName}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm" c="dimmed">
                                                        {delegation.healthcareProfessionalTypes?.find(
                                                            type => type.id === delegation.delegatedHcpSpecialty
                                                        )?.name || t('global.unknown')}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    {getConsentStatusBadge(delegation.patientConsentStatus)}
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {new Date(delegation.createdDate).toLocaleDateString()}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    {delegation.canRevoke && (
                                                        <Tooltip label={t('delegation.revoke.tooltip')}>
                                                            <ActionIcon
                                                                color="red"
                                                                variant="subtle"
                                                                onClick={() => handleRevokeDelegation(delegation)}
                                                            >
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    )}
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    )}
                </Stack>
            </Paper>
        );
    })
);

const WrappedInternalCollaboratorTable: React.FC = () => {
    return (
        <TableContextProvider>
            <InternalCollaboratorTable />
        </TableContextProvider>
    );
};

export default WrappedInternalCollaboratorTable;
